import { Skeleton } from '@/components/ui/skeleton';
import { GateReportViewer } from './GateReportViewer';
import { HumanReviewTab } from './review';
import { ReportTabBar } from './ReportTabBar';
import type { Job, GateReport } from '@/types';

/**
 * Presentational sections of the job detail page.
 *
 * Split out of JobDetailPage on 2026-07-16 for CC-10 (the page was 313 lines
 * against a 300 cap). Both components are pure — all state and fetching stay
 * on the page.
 */

export function JobDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-5 w-24" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

interface ReportSectionProps {
  job: Job;
  report: GateReport;
  activeTab: 'report' | 'review';
  canReview: boolean;
  onTabChange: (tab: 'report' | 'review') => void;
  onReviewClaimed: () => void;
}

/** Gate report + human review, behind a tab bar when the user can review. */
export function ReportSection({
  job,
  report,
  activeTab,
  canReview,
  onTabChange,
  onReviewClaimed,
}: ReportSectionProps) {
  return (
    <>
      {canReview && (
        <ReportTabBar
          activeTab={activeTab}
          hasInReviewIndicator={job.review_status === 'in_progress' || job.review_status === 'claimed'}
          onChange={onTabChange}
        />
      )}
      <div role="tabpanel" id="tab-panel-report" aria-labelledby="tab-report" hidden={activeTab !== 'report'}>
        <GateReportViewer job={job} report={report} onReviewClaimed={onReviewClaimed} />
      </div>
      {canReview && (
        <div role="tabpanel" id="tab-panel-review" aria-labelledby="tab-review" hidden={activeTab !== 'review'}>
          <HumanReviewTab job={job} report={report} />
        </div>
      )}
    </>
  );
}
